import React, { useContext, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { useTheme } from '@material-ui/core';
import {
  APIAlert,
  FlexColumnDiv,
  GridDownloadButton,
} from '@mummssoftware/common-ui';
import { GlobalContext } from '@mummssoftware/global-context';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import styled from 'styled-components';
import { getColumnDefs } from './columnDefs';
import { IncidentsContext } from '../../context';

import { IncidentEditButtons } from './IncidentEditButtons';
import { getBlankIncident } from './utils';
import { IncidentConfirmModal } from './IncidentConfirmModal';
import { postFailure, postSuccess, SELECTING } from './constants';

const GridWrapper = styled.div<{
  printMode?: boolean;
  name?: string;
}>`
  background-color: ${(props) => props.theme.palette.background.paper};
  overflow: auto;
  height: 90%;
  flex-grow: 1;
`;

const IncidentButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
`;

export type InputMode = 'SELECTING' | 'TYPING';

export const EditContext = React.createContext(
  {} as {
    editState: any;
    setEditState: (a: any) => void;
    typeInputMode: InputMode; // is the user selecting a predefined type or entering a new one
    setTypeInputMode: (a: InputMode) => void;
  },
);

export const IncidentGrid = ({
  incidents,
  incidentTypes,
}: {
  incidents: mumms.Incident[];
  incidentTypes: string[];
}) => {
  const {
    smallWidget,
    patient,
    currentUser,
    patientNumber,
    observation,
    agency,
    siteId,
  } = useContext(IncidentsContext);

  const { appVisit } = useContext(GlobalContext);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [rowData, setRowData] = useState(incidents);

  const [editState, setEditState] = useState<Partial<mumms.Incident> | any>(
    null,
  );
  const [typeInputMode, setTypeInputMode] = useState<InputMode>(SELECTING);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const [alert, setAlert] = useState<null | mumms.ErrorInfo>(null);
  const closeAlert = () => setAlert(null);

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      singleClickEdit: true,
      sortable: true,
      menuTabs: ['filterMenuTab'],
    },
    columnDefs: getColumnDefs(smallWidget, !patientNumber, incidentTypes),
    onGridReady: (params: any) => {
      setGridApi(params.api);
      params.api.sizeColumnsToFit();
    },
  };

  const updateRowData = (data: any) => {
    setRowData(data);
    if (gridApi) {
      gridApi.setRowData(data);
    }
  };

  const appendEditsToGrid = () => {
    updateRowData([editState, ...rowData]);
  };

  const clearEditState = () => {
    setEditState(null);
    setTypeInputMode(SELECTING);
  };

  const submitEdits = () => {
    observation
      .postIncident(editState, agency, siteId || '')
      .then(() => {
        setAlert(postSuccess);
        appendEditsToGrid();
      })
      .catch(() => setAlert(postFailure))
      .finally(clearEditState);
  };

  const onAddClick = () => {
    if (siteId) {
      const incident = getBlankIncident(
        currentUser,
        patient as mumms.patient,
        siteId,
        appVisit,
      );
      setEditState(incident);
    }
  };

  const onSaveClick = () => {
    if (editState.details) {
      submitEdits();
    } else {
      openConfirmModal();
    }
  };

  return (
    <EditContext.Provider
      value={{ editState, setEditState, typeInputMode, setTypeInputMode }}
    >
      {alert && <APIAlert handleClose={closeAlert} errorInfo={alert} />}

      <FlexColumnDiv style={{ height: 'inherit' }}>
        {!smallWidget && (
          <IncidentButtonsWrapper>
            {patient && (
              <IncidentEditButtons
                editState={editState}
                onAdd={onAddClick}
                onSave={onSaveClick}
                onCancel={clearEditState}
              />
            )}
            {gridApi && (
              <GridDownloadButton gridApi={gridApi} includeCSV includeExcel />
            )}
          </IncidentButtonsWrapper>
        )}

        {confirmModalOpen && (
          <IncidentConfirmModal
            open={confirmModalOpen}
            onConfirm={() => {
              submitEdits();
              closeConfirmModal();
            }}
            onClose={closeConfirmModal}
          />
        )}

        <GridWrapper
          id="incidents-table-wrapper"
          className={`${gridTheme.className} light`}
        >
          <AgGridReact
            gridOptions={gridOptions}
            rowData={rowData}
            rowHeight={gridTheme.rowHeight}
            headerHeight={gridTheme.headerHeight}
            pinnedTopRowData={editState ? [editState] : undefined} // *
          />
        </GridWrapper>
      </FlexColumnDiv>
    </EditContext.Provider>
  );
};
